import { useEffect, useRef, useState } from 'react';

interface Props {
    isRunning: boolean;
    isPaused: boolean;
}

const TICK_MS = 200;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 3600;

const pad = (value: number) => (value < 10 ? `0${value}` : value.toString());

/**
 * Секундомер записи.
 *
 * Время считается по реальным часам, а не сложением тиков `setInterval`:
 * шаг интервала не гарантирован (в фоновой вкладке браузер его тормозит),
 * и счётчик расходился бы с фактической длительностью записи.
 */
export const useStopwatch = ({ isRunning, isPaused }: Props) => {
    const [elapsedMs, setElapsedMs] = useState(0);

    /** Накоплено до текущей паузы. */
    const accumulatedRef = useRef(0);
    /** Момент запуска текущего отрезка; null — отрезок не идёт. */
    const startedAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!isRunning) {
            accumulatedRef.current = 0;
            startedAtRef.current = null;
            setElapsedMs(0);
            return undefined;
        }

        if (isPaused) {
            if (startedAtRef.current !== null) {
                accumulatedRef.current += performance.now() - startedAtRef.current;
                startedAtRef.current = null;
                setElapsedMs(accumulatedRef.current);
            }
            return undefined;
        }

        startedAtRef.current = performance.now();

        const tick = () => {
            const startedAt = startedAtRef.current;
            if (startedAt === null) return;
            setElapsedMs(accumulatedRef.current + (performance.now() - startedAt));
        };

        tick();
        const interval = setInterval(tick, TICK_MS);

        return () => clearInterval(interval);
    }, [isRunning, isPaused]);

    const totalSeconds = Math.floor(elapsedMs / 1000);
    const hours = Math.floor(totalSeconds / SECONDS_IN_HOUR);
    const minutes = Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);
    const seconds = totalSeconds % SECONDS_IN_MINUTE;

    return {
        hours: pad(hours),
        minutes: pad(minutes),
        seconds: pad(seconds),
        /** Готовая строка: часы появляются только когда они есть. */
        time: hours > 0
            ? `${hours}:${pad(minutes)}:${pad(seconds)}`
            : `${pad(minutes)}:${pad(seconds)}`,
        elapsedSeconds: totalSeconds,
    };
};
